import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import { lifeTaggerApi } from '../api/lifetaggerApi';
import { lifeTaggerDesign } from '../design/tokens';
import { audiencePresentation } from './audiencePresentation';

const choices = [
  [
    'public',
    'Everyone',
    'Anyone who meets all discovery requirements.',
    PublicRoundedIcon,
  ],
  ['private', 'Only me', 'Keep this just for yourself.', LockOutlinedIcon],
  [
    'followers',
    'My followers & subscribers',
    'People following in the app or subscribed in a browser.',
    GroupsRoundedIcon,
  ],
  [
    'named-users',
    'Selected followers & subscribers',
    'Choose who this is for.',
    PersonSearchRoundedIcon,
  ],
];

export default function AudiencePicker({ audience, onChange, onAudienceData }) {
  const [data, setData] = React.useState({ items: [] });
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  React.useEffect(() => {
    let active = true;
    lifeTaggerApi
      .listBrowserSubscribers()
      .then((result) => {
        if (!active) return;
        setData(result);
        onAudienceData?.(result);
      })
      .catch(() => {
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [onAudienceData]);
  const ids = audience.identifiers || [];
  const legacy =
    !choices.some((choice) => choice[0] === audience.visibility) ||
    (audience.visibility === 'named-users' &&
      audience.requireSubscription !== true);
  const presentation = audiencePresentation(audience, data);
  const options = data.items || [];
  const selected = ids.map(
    (id) =>
      options.find((item) => item.id === id) || {
        id,
        name: 'Unavailable subscriber',
      }
  );
  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          value={legacy ? '' : audience.visibility}
          onChange={(event) =>
            onChange({
              visibility: event.target.value,
              identifiers: [],
              ...(event.target.value === 'named-users'
                ? { requireSubscription: true }
                : {}),
            })
          }
          aria-label="Who can discover this LifeObject?"
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 1,
            }}
          >
            {choices.map(([value, label, detail, Icon]) => (
              <Box
                component="label"
                key={value}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor:
                    !legacy && audience.visibility === value
                      ? '#E96842'
                      : lifeTaggerDesign.color.border,
                  borderRadius: '14px',
                  bgcolor: '#FFFCF7',
                  cursor: 'pointer',
                  '&:focus-within': { outline: '3px solid #E9684244' },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Radio value={value} size="small" sx={{ p: 0.25 }} />
                  <Icon fontSize="small" aria-hidden="true" />
                  <Box>
                    <Typography fontWeight={700}>{label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {detail}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Box>
        </RadioGroup>
      </FormControl>
      {legacy && (
        <Alert severity="info">
          {presentation.label}: your saved audience is unchanged. Choose an
          option above only if you want to replace it.
        </Alert>
      )}
      {loading && (
        <Stack direction="row" spacing={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading subscribers…</Typography>
        </Stack>
      )}
      {loadError && (
        <Alert severity="warning">
          Your subscribers could not be loaded. Your saved selection is
          unchanged.
        </Alert>
      )}
      {!legacy && audience.visibility === 'named-users' && (
        <Autocomplete
          multiple
          options={options}
          value={selected}
          loading={loading}
          disabled={loadError || loading}
          getOptionLabel={(item) => item.name || 'Browser subscriber'}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText="No subscribers yet"
          onChange={(_event, people) =>
            onChange({
              visibility: 'named-users',
              requireSubscription: true,
              identifiers: people.map((item) => item.id),
            })
          }
          renderTags={(value, getTagProps) =>
            value.map((item, index) => (
              <Chip
                key={item.id}
                label={item.name}
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search followers & subscribers"
              helperText="Unnamed browsers appear as Browser subscriber. Access is tied to their account or browser subscription."
            />
          )}
        />
      )}
      <Alert severity="info">
        {presentation.reach}. Every attached discovery requirement must also
        match.
      </Alert>
    </Stack>
  );
}
